import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubApi } from "../../../club-api-client";
import type {
	CreateClubTournament,
	MarkClubTournamentWalkover,
	RegisterClubTournamentTeam,
	SeedClubTournament,
	SetClubTournamentMemberLicense,
	SetClubTournamentVisibility,
	UpdateClubTournament,
} from "../../../types";
import { queryKeys } from "../../query-keys";

export type ClubTournamentListParams = {
	q?: string;
	limit?: number;
	offset?: number;
	club_id?: string;
	status?: string;
	visibility?: string[];
	from?: string;
	to?: string;
};

export function useClubTournaments(params?: ClubTournamentListParams) {
	return useQuery({
		queryKey: queryKeys.clubTournaments.list(params),
		queryFn: () => clubApi.tournaments.list(params),
		placeholderData: (prev) => prev,
	});
}

export function useClubTournamentDetail(id: string | null) {
	return useQuery({
		queryKey: queryKeys.clubTournaments.detail(id ?? ""),
		queryFn: () => clubApi.tournaments.get(id!),
		enabled: !!id,
	});
}

export function useCreateClubTournament() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateClubTournament) => clubApi.tournaments.create(data),
		onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.all }),
	});
}

export function useUpdateClubTournament() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateClubTournament }) =>
			clubApi.tournaments.update(id, data),
		onSuccess: (_, { id }) => {
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.all });
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) });
		},
	});
}

export function useSetClubTournamentVisibility() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: SetClubTournamentVisibility }) =>
			clubApi.tournaments.setVisibility(id, data),
		onSuccess: (_, { id }) => {
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.all });
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) });
		},
	});
}

export function useOpenClubTournamentRegistration() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => clubApi.tournaments.openRegistration(id),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.all });
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) });
		},
	});
}

export function useCloseClubTournamentRegistration() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => clubApi.tournaments.closeRegistration(id),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.all });
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) });
		},
	});
}

export function useRegisterClubTournamentTeam() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: RegisterClubTournamentTeam }) =>
			clubApi.tournaments.registerTeam(id, data),
		onSuccess: (_, { id }) =>
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) }),
	});
}

export function useWithdrawClubTournamentTeam() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, teamId }: { id: string; teamId: string }) =>
			clubApi.tournaments.withdrawTeam(id, teamId),
		onSuccess: (_, { id }) =>
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) }),
	});
}

export function useCheckInClubTournamentTeam() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, teamId }: { id: string; teamId: string }) =>
			clubApi.tournaments.checkIn(id, teamId),
		onSuccess: (_, { id }) =>
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) }),
	});
}

// Search the discovery athlete directory for the DVV link picker. Beach-only data;
// caller passes federation: "dvv" (or none) and a debounced query.
export function useClubAthleteDirectory(
	q: string,
	opts?: { federation?: string; enabled?: boolean; limit?: number },
) {
	const limit = opts?.limit ?? 10;
	return useQuery({
		queryKey: ["athletes", "directory", q, opts?.federation ?? "all", limit] as const,
		queryFn: () => clubApi.athletes.search({ q, federation: opts?.federation, limit }),
		enabled: (opts?.enabled ?? true) && q.trim().length >= 2,
		placeholderData: (prev) => prev,
	});
}

export function useSetClubTournamentMemberLicense() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			teamId,
			memberId,
			data,
		}: {
			id: string;
			teamId: string;
			memberId: string;
			data: SetClubTournamentMemberLicense;
		}) => clubApi.tournaments.setMemberLicense(id, teamId, memberId, data),
		onSuccess: (_, { id }) =>
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) }),
	});
}

export function useRemoveClubTournamentMemberLicense() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, teamId, memberId }: { id: string; teamId: string; memberId: string }) =>
			clubApi.tournaments.removeMemberLicense(id, teamId, memberId),
		onSuccess: (_, { id }) =>
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) }),
	});
}

export function useSeedClubTournament() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: SeedClubTournament }) =>
			clubApi.tournaments.seed(id, data),
		onSuccess: (_, { id }) =>
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) }),
	});
}

export function useGenerateClubTournamentBracket() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => clubApi.tournaments.generateBracket(id),
		onSuccess: (_, id) => qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) }),
	});
}

export function useStartClubTournament() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => clubApi.tournaments.start(id),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.all });
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) });
		},
	});
}

export function useMarkClubTournamentWalkover() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			nodeId,
			data,
		}: {
			id: string;
			nodeId: string;
			data: MarkClubTournamentWalkover;
		}) => clubApi.tournaments.markWalkover(id, nodeId, data),
		onSuccess: (_, { id }) =>
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) }),
	});
}

export function useCompleteClubTournament() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => clubApi.tournaments.complete(id),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.all });
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) });
		},
	});
}

export function useCancelClubTournament() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => clubApi.tournaments.cancel(id),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.all });
			qc.invalidateQueries({ queryKey: queryKeys.clubTournaments.detail(id) });
		},
	});
}
